import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState, useMemo } from 'react';
import { SimulationStatistic, SiteData } from '../types';
import { TreePine, Building2, Users, Activity, ArrowLeft, RotateCcw, ChevronDown } from 'lucide-react';

interface SimulationStatisticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  simulationStats: SimulationStatistic[];
  siteData: SiteData[];
}

interface TreemapNode {
  name: string;
  value: number;
  children?: TreemapNode[];
  color?: string;
}

export const SimulationStatisticsModal = ({ isOpen, onClose, simulationStats, siteData }: SimulationStatisticsModalProps) => {
  const [selectedSimulatorType, setSelectedSimulatorType] = useState<string>('');
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [drilldownLevel, setDrilldownLevel] = useState<'simulator' | 'module' | 'case' | 'site' | 'site-simulator' | 'site-module'>('simulator');
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [selectedSiteSimulator, setSelectedSiteSimulator] = useState<string>('');
  
  // Filter states - now arrays for multiple selection
  const [filterSimulatorType, setFilterSimulatorType] = useState<string[]>([]);
  const [filterRegion, setFilterRegion] = useState<string[]>([]);

  // Get unique values for filters
  const availableSimulatorTypes = useMemo(() => 
    [...new Set(simulationStats.map(stat => stat.simulatorType).filter(type => type && type.trim() !== ''))].sort(),
    [simulationStats]
  );
  
  // Get unique regions by joining simulation stats with site data
  const availableRegions = useMemo(() => {
    const regions = simulationStats.map(stat => {
      const matchingSite = siteData.find(site => site.siteName === stat.siteName);
      if (!matchingSite) return null;
      
      // Use country/region logic
      if (matchingSite.country === 'United States') {
        return 'North America';
      } else {
        return matchingSite.region;
      }
    }).filter(region => region && region.trim() !== '');
    
    return [...new Set(regions)].sort();
  }, [simulationStats, siteData]);

  // Apply filters to simulation stats
  const filteredSimulationStats = useMemo(() => {
    return simulationStats.filter(stat => {
      const matchesSimulatorType = filterSimulatorType.length === 0 || filterSimulatorType.includes(stat.simulatorType);
      
      // Join with site data to get region info for filtering
      const matchingSite = siteData.find(site => site.siteName === stat.siteName);
      if (!matchingSite && filterRegion.length > 0) return false;
      
      // Use country/region logic for filtering
      let statRegionDisplay;
      if (matchingSite?.country === 'United States') {
        statRegionDisplay = 'North America';
      } else {
        statRegionDisplay = matchingSite?.region;
      }
      
      const matchesRegion = filterRegion.length === 0 || filterRegion.includes(statRegionDisplay);
      return matchesSimulatorType && matchesRegion;
    });
  }, [simulationStats, filterSimulatorType, filterRegion, siteData]);

  // Reset all filters
  const resetFilters = () => {
    setFilterSimulatorType([]);
    setFilterRegion([]);
    resetSimulatorDrilldown();
    resetSiteDrilldown();
  };

  // Generate mock case data based on module
  const generateCaseData = (module: string, totalSessions: number) => {
    const caseTemplates = [
      'Basic Training',
      'Advanced Technique',
      'Emergency Scenario',
      'Routine Procedure',
      'Complex Case',
      'Beginner Level',
      'Expert Challenge',
      'Assessment Mode'
    ];
    
    const numCases = Math.min(caseTemplates.length, Math.floor(Math.random() * 6) + 3);
    const cases = [];
    let remainingSessions = totalSessions;
    
    for (let i = 0; i < numCases; i++) {
      const isLast = i === numCases - 1;
      const sessions = isLast ? remainingSessions : Math.floor(Math.random() * (remainingSessions / (numCases - i))) + 1;
      
      cases.push({
        name: `${caseTemplates[i]} - ${module}`,
        value: sessions,
        sessions: sessions
      });
      
      remainingSessions -= sessions;
    }
    
    return cases.sort((a, b) => b.value - a.value);
  };

  // Process data for simulator type treemap (Tab 1) - using last 12 months data
  const simulatorTypeData = useMemo(() => {
    if (drilldownLevel === 'simulator') {
      // Group by simulator type using last 12 months data
      const grouped = filteredSimulationStats.reduce((acc, stat) => {
        if (!acc[stat.simulatorType]) {
          acc[stat.simulatorType] = {
            name: stat.simulatorType,
            value: 0,
            sessions: 0
          };
        }
        acc[stat.simulatorType].value += stat.sessionsLast12Months;
        acc[stat.simulatorType].sessions += stat.sessionsLast12Months;
        return acc;
      }, {} as Record<string, any>);

      return Object.values(grouped).sort((a: any, b: any) => b.value - a.value);
    } else if (drilldownLevel === 'module') {
      // Drill down to modules for selected simulator type
      const filteredStats = filteredSimulationStats.filter(stat => stat.simulatorType === selectedSimulatorType);
      const grouped = filteredStats.reduce((acc, stat) => {
        if (!acc[stat.moduleDescription]) {
          acc[stat.moduleDescription] = {
            name: stat.moduleDescription,
            value: 0,
            sessions: 0
          };
        }
        acc[stat.moduleDescription].value += stat.sessionsLast12Months;
        acc[stat.moduleDescription].sessions += stat.sessionsLast12Months;
        return acc;
      }, {} as Record<string, any>);

      return Object.values(grouped).sort((a: any, b: any) => b.value - a.value);
    } else if (drilldownLevel === 'case') {
      // Drill down to cases for selected module
      const filteredStats = filteredSimulationStats.filter(stat => 
        stat.simulatorType === selectedSimulatorType && stat.moduleDescription === selectedModule
      );
      const totalSessions = filteredStats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
      
      return generateCaseData(selectedModule, totalSessions);
    }
    return [];
  }, [filteredSimulationStats, drilldownLevel, selectedSimulatorType, selectedModule]);

  // Process data for sites treemap (Tab 2) - using last 12 months data
  const sitesData = useMemo(() => {
    if (drilldownLevel === 'site') {
      // Group by site name using last 12 months data
      const grouped = filteredSimulationStats.reduce((acc, stat) => {
        if (!acc[stat.siteName]) {
          acc[stat.siteName] = {
            name: stat.siteName,
            value: 0,
            sessions: 0,
            customer: stat.customer,
            region: stat.region
          };
        }
        acc[stat.siteName].value += stat.sessionsLast12Months;
        acc[stat.siteName].sessions += stat.sessionsLast12Months;
        return acc;
      }, {} as Record<string, any>);

      return Object.values(grouped).sort((a: any, b: any) => b.value - a.value);
    } else if (drilldownLevel === 'site-simulator') {
      // Drill down to simulator types for selected site
      const filteredStats = filteredSimulationStats.filter(stat => stat.siteName === selectedSite);
      const grouped = filteredStats.reduce((acc, stat) => {
        if (!acc[stat.simulatorType]) {
          acc[stat.simulatorType] = {
            name: stat.simulatorType,
            value: 0,
            sessions: 0
          };
        }
        acc[stat.simulatorType].value += stat.sessionsLast12Months;
        acc[stat.simulatorType].sessions += stat.sessionsLast12Months;
        return acc;
      }, {} as Record<string, any>);

      return Object.values(grouped).sort((a: any, b: any) => b.value - a.value);
    } else if (drilldownLevel === 'site-module') {
      // Drill down to modules for selected site and simulator
      const filteredStats = filteredSimulationStats.filter(stat => 
        stat.siteName === selectedSite && stat.simulatorType === selectedSiteSimulator
      );
      const grouped = filteredStats.reduce((acc, stat) => {
        if (!acc[stat.moduleDescription]) {
          acc[stat.moduleDescription] = {
            name: stat.moduleDescription,
            value: 0,
            sessions: 0
          };
        }
        acc[stat.moduleDescription].value += stat.sessionsLast12Months;
        acc[stat.moduleDescription].sessions += stat.sessionsLast12Months;
        return acc;
      }, {} as Record<string, any>);

      return Object.values(grouped).sort((a: any, b: any) => b.value - a.value);
    }
    return [];
  }, [filteredSimulationStats, drilldownLevel, selectedSite, selectedSiteSimulator]);

  const handleSimulatorClick = (data: any) => {
    if (drilldownLevel === 'simulator') {
      setSelectedSimulatorType(data.name);
      setDrilldownLevel('module');
    } else if (drilldownLevel === 'module') {
      setSelectedModule(data.name);
      setDrilldownLevel('case');
    }
  };

  const handleSiteClick = (data: any) => {
    if (drilldownLevel === 'site') {
      setSelectedSite(data.name);
      setDrilldownLevel('site-simulator');
    } else if (drilldownLevel === 'site-simulator') {
      setSelectedSiteSimulator(data.name);
      setDrilldownLevel('site-module');
    }
  };

  const resetSimulatorDrilldown = () => {
    setDrilldownLevel('simulator');
    setSelectedSimulatorType('');
    setSelectedModule('');
  };

  const resetSiteDrilldown = () => {
    setDrilldownLevel('site');
    setSelectedSite('');
    setSelectedSiteSimulator('');
  };

  const getRandomColor = (index: number) => {
    const colors = [
      '#3B82F6', // blue
      '#EF4444', // red
      '#10B981', // emerald
      '#F59E0B', // amber
      '#8B5CF6', // violet
      '#EC4899', // pink
      '#06B6D4', // cyan
      '#84CC16', // lime
      '#F97316', // orange
      '#6366F1'  // indigo
    ];
    return colors[index % colors.length];
  };

  // Custom Treemap Component with proper layout algorithm
  const TreemapChart = ({ data, onNodeClick }: { data: any[], onNodeClick?: (node: any) => void }) => {
    // Group small items into "Other" category
    const processedData = useMemo(() => {
      const mainItems = data.filter(item => item.value >= 10);
      const smallItems = data.filter(item => item.value < 10);
      
      if (smallItems.length === 0) return data;
      
      const otherTotal = smallItems.reduce((sum, item) => sum + item.value, 0);
      if (otherTotal === 0) return mainItems;
      
      const otherItem = {
        name: 'Other',
        value: otherTotal,
        sessions: otherTotal,
        isOther: true,
        otherItems: smallItems
      };
      
      return [...mainItems, otherItem];
    }, [data]);

    const totalValue = processedData.reduce((sum, item) => sum + item.value, 0);
    
    // Natural asymmetric treemap algorithm based on squarified layout
    const squarify = (children: any[], x: number, y: number, width: number, height: number): any[] => {
      if (children.length === 0) return [];
      
      const total = children.reduce((sum, child) => sum + child.value, 0);
      if (total === 0) return [];
      
      // Sort by value descending for better packing
      const sorted = [...children].sort((a, b) => b.value - a.value);
      
      return squarifyRecursive(sorted, x, y, width, height, total);
    };

    const squarifyRecursive = (
      children: any[], 
      x: number, 
      y: number, 
      width: number, 
      height: number, 
      total: number
    ): any[] => {
      if (children.length === 0) return [];
      
      if (children.length === 1) {
        return [{
          ...children[0],
          x,
          y,
          width,
          height,
          color: children[0].isOther ? '#9CA3AF' : getRandomColor(Math.floor(Math.random() * 10))
        }];
      }

      // Find the best split point
      const bestSplit = findBestSplit(children, width, height);
      const { leftChildren, rightChildren, isVerticalSplit, splitRatio } = bestSplit;
      
      const result: any[] = [];
      
      if (isVerticalSplit) {
        // Split vertically
        const leftWidth = width * splitRatio;
        const rightWidth = width - leftWidth;
        
        result.push(...squarifyRecursive(leftChildren, x, y, leftWidth, height, total));
        result.push(...squarifyRecursive(rightChildren, x + leftWidth, y, rightWidth, height, total));
      } else {
        // Split horizontally
        const topHeight = height * splitRatio;
        const bottomHeight = height - topHeight;
        
        result.push(...squarifyRecursive(leftChildren, x, y, width, topHeight, total));
        result.push(...squarifyRecursive(rightChildren, x, y + topHeight, width, bottomHeight, total));
      }
      
      return result;
    };

    const findBestSplit = (children: any[], width: number, height: number) => {
      const total = children.reduce((sum, child) => sum + child.value, 0);
      let bestScore = Infinity;
      let bestSplit = {
        leftChildren: children.slice(0, 1),
        rightChildren: children.slice(1),
        isVerticalSplit: width > height,
        splitRatio: 0.5
      };
      
      // Try different split points
      for (let i = 1; i < children.length; i++) {
        const leftChildren = children.slice(0, i);
        const rightChildren = children.slice(i);
        const leftSum = leftChildren.reduce((sum, child) => sum + child.value, 0);
        const rightSum = rightChildren.reduce((sum, child) => sum + child.value, 0);
        const splitRatio = leftSum / total;
        
        // Try both vertical and horizontal splits
        for (const isVertical of [true, false]) {
          let score = 0;
          
          if (isVertical) {
            const leftWidth = width * splitRatio;
            const rightWidth = width - leftWidth;
            
            // Calculate aspect ratio penalty for each side
            const leftAspect = leftWidth / height;
            const rightAspect = rightWidth / height;
            
            // Prefer aspect ratios closer to golden ratio (1.618) or reasonable ranges
            score += Math.abs(leftAspect - 1.6) + Math.abs(rightAspect - 1.6);
            
            // Penalize extreme aspect ratios
            if (leftAspect < 0.3 || leftAspect > 5) score += 10;
            if (rightAspect < 0.3 || rightAspect > 5) score += 10;
            
          } else {
            const topHeight = height * splitRatio;
            const bottomHeight = height - topHeight;
            
            const topAspect = width / topHeight;
            const bottomAspect = width / bottomHeight;
            
            score += Math.abs(topAspect - 1.6) + Math.abs(bottomAspect - 1.6);
            
            if (topAspect < 0.3 || topAspect > 5) score += 10;
            if (bottomAspect < 0.3 || bottomAspect > 5) score += 10;
          }
          
          // Add balance penalty (prefer more balanced splits)
          const balance = Math.min(splitRatio, 1 - splitRatio);
          score += (0.5 - balance) * 2;
          
          if (score < bestScore) {
            bestScore = score;
            bestSplit = {
              leftChildren,
              rightChildren,
              isVerticalSplit: isVertical,
              splitRatio
            };
          }
        }
      }
      
      return bestSplit;
    };

    const layoutNodes = squarify(processedData, 0, 0, 100, 55); // Optimized aspect ratio

    return (
      <div className="relative w-full h-[500px] border border-border rounded-lg overflow-hidden bg-muted/10">
        <svg width="100%" height="100%" viewBox="0 0 100 55" className="w-full h-full">
          {layoutNodes.map((node, index) => (
            <g key={`${node.name}-${index}`}>
              <rect
                x={node.x}
                y={node.y}
                width={node.width}
                height={node.height}
                fill={node.color}
                stroke="#ffffff"
                strokeWidth={0.1}
                className={onNodeClick && !node.isOther ? "cursor-pointer hover:opacity-90 transition-all duration-200 hover:stroke-white/60 hover:stroke-1" : node.isOther ? "cursor-help" : ""}
                onClick={() => onNodeClick && !node.isOther && onNodeClick(node)}
              >
                {/* Tooltip for all items */}
                <title>
                  {node.isOther 
                    ? `Other items (${node.otherItems?.length}): ${node.otherItems?.map((item: any) => `${item.name}: ${item.value}`).join(', ')}`
                    : `${node.name}: ${node.value.toLocaleString()} sessions`
                  }
                </title>
              </rect>
              
              {/* Responsive text labels */}
              {node.width > 4 && node.height > 3 && (
                <>
                  {/* Main label */}
                  <text
                    x={node.x + node.width / 2}
                    y={node.y + node.height / 2 - 0.8}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize={Math.max(0.6, Math.min(node.width / 8, node.height / 3, 1.8))}
                    fontWeight="600"
                    className="pointer-events-none select-none"
                  >
                    <tspan x={node.x + node.width / 2} dy="0">
                      {node.width > 10 && node.height > 5 
                        ? (node.name.length > 18 ? node.name.substring(0, 16) + '...' : node.name)
                        : node.width > 6 && node.height > 3
                        ? (node.name.length > 12 ? node.name.substring(0, 10) + '...' : node.name)
                        : (node.name.length > 6 ? node.name.substring(0, 4) + '...' : node.name)
                      }
                    </tspan>
                  </text>
                  
                  {/* Value label - only show if there's enough space */}
                  {node.width > 6 && node.height > 4.5 && (
                    <text
                      x={node.x + node.width / 2}
                      y={node.y + node.height / 2 + 1}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="white"
                      fontSize={Math.max(0.5, Math.min(node.width / 10, node.height / 4, 1.4))}
                      className="pointer-events-none select-none"
                      opacity="0.9"
                    >
                      {node.value >= 1000 ? `${(node.value / 1000).toFixed(1)}k` : node.value}
                    </text>
                  )}
                </>
              )}
              
              {/* Minimal label for very small rectangles */}
              {(node.width <= 4 || node.height <= 3) && node.width > 2 && node.height > 1.5 && (
                <text
                  x={node.x + node.width / 2}
                  y={node.y + node.height / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  fontSize={Math.max(0.5, Math.min(node.width / 4, node.height / 2, 1.2))}
                  fontWeight="600"
                  className="pointer-events-none select-none"
                >
                  {node.value >= 1000 ? `${Math.floor(node.value / 1000)}k` : node.value}
                </text>
              )}
            </g>
          ))}
        </svg>
        
        {/* Info tooltip */}
        <div className="absolute bottom-2 left-2 text-xs text-muted-foreground bg-background/90 p-2 rounded shadow-sm">
          {layoutNodes.some(node => node.isOther) && "Hover rectangles for details • "}
          {onNodeClick ? "Click to drill down" : "Session data visualization"}
        </div>
      </div>
    );
  };

  const totalSessions = filteredSimulationStats.reduce((sum, stat) => sum + stat.sessionsLast12Months, 0);
  const uniqueSimulators = new Set(filteredSimulationStats.map(stat => stat.simulatorType)).size;
  const uniqueSites = new Set(filteredSimulationStats.map(stat => stat.siteName)).size;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-3">
            <TreePine className="w-6 h-6 text-primary" />
            Simulation Statistics
            <Badge variant="outline" className="ml-2">
              Last 12 Months Data
            </Badge>
          </DialogTitle>
          
          {/* Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4 pb-2 border-t mt-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-muted-foreground">Filters:</span>
              
              {/* Multi-select Simulator Type Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px] justify-between h-9">
                    {filterSimulatorType.length === 0 
                      ? "All Simulator Types" 
                      : filterSimulatorType.length === 1
                      ? filterSimulatorType[0]
                      : `${filterSimulatorType.length} types selected`
                    }
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0">
                  <div className="p-2">
                    <div className="flex items-center space-x-2 p-2">
                      <Checkbox
                        id="all-simulators"
                        checked={filterSimulatorType.length === 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFilterSimulatorType([]);
                          }
                        }}
                      />
                      <label htmlFor="all-simulators" className="text-sm font-medium">
                        All Simulator Types
                      </label>
                    </div>
                    <Separator className="my-1" />
                    {availableSimulatorTypes.map((type) => (
                      <div key={type} className="flex items-center space-x-2 p-2">
                        <Checkbox
                          id={`simulator-${type}`}
                          checked={filterSimulatorType.includes(type)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFilterSimulatorType([...filterSimulatorType, type]);
                            } else {
                              setFilterSimulatorType(filterSimulatorType.filter(t => t !== type));
                            }
                          }}
                        />
                        <label htmlFor={`simulator-${type}`} className="text-sm">
                          {type}
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Multi-select Region Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[160px] justify-between h-9">
                    {filterRegion.length === 0 
                      ? "All Regions" 
                      : filterRegion.length === 1
                      ? filterRegion[0]
                      : `${filterRegion.length} regions selected`
                    }
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[160px] p-0">
                  <div className="p-2">
                    <div className="flex items-center space-x-2 p-2">
                      <Checkbox
                        id="all-regions"
                        checked={filterRegion.length === 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFilterRegion([]);
                          }
                        }}
                      />
                      <label htmlFor="all-regions" className="text-sm font-medium">
                        All Regions
                      </label>
                    </div>
                    <Separator className="my-1" />
                    {availableRegions.map((region) => (
                      <div key={region} className="flex items-center space-x-2 p-2">
                        <Checkbox
                          id={`region-${region}`}
                          checked={filterRegion.includes(region)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFilterRegion([...filterRegion, region]);
                            } else {
                              setFilterRegion(filterRegion.filter(r => r !== region));
                            }
                          }}
                        />
                        <label htmlFor={`region-${region}`} className="text-sm">
                          {region}
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {(filterSimulatorType.length > 0 || filterRegion.length > 0) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={resetFilters}
                  className="flex items-center gap-2 h-9"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset
                </Button>
              )}
            </div>

            {(filterSimulatorType.length > 0 || filterRegion.length > 0) && (
              <div className="text-xs text-muted-foreground self-center">
                Showing {filteredSimulationStats.length} of {simulationStats.length} records
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 shadow-card">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Total Sessions (12m)</p>
                <p className="text-2xl font-bold">{totalSessions.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 shadow-card">
            <div className="flex items-center gap-3">
              <TreePine className="w-8 h-8 text-secondary" />
              <div>
                <p className="text-sm text-muted-foreground">Unique Simulators</p>
                <p className="text-2xl font-bold">{uniqueSimulators}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 shadow-card">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-success" />
              <div>
                <p className="text-sm text-muted-foreground">Active Sites</p>
                <p className="text-2xl font-bold">{uniqueSites}</p>
              </div>
            </div>
          </Card>
        </div>

        <Tabs defaultValue="simulators" className="w-full" onValueChange={(value) => {
          // Reset drill down states when switching tabs
          if (value === 'simulators') {
            setDrilldownLevel('simulator');
            setSelectedSimulatorType('');
            setSelectedModule('');
          } else if (value === 'sites') {
            setDrilldownLevel('site');
            setSelectedSite('');
            setSelectedSiteSimulator('');
          }
        }}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="simulators">Sessions by Simulator Type</TabsTrigger>
            <TabsTrigger value="sites">Sessions by Sites</TabsTrigger>
          </TabsList>
          
          <TabsContent value="simulators">
            <Card className="p-6 shadow-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                   <h3 className="text-lg font-semibold">
                     {drilldownLevel === 'case' ? 'Case Descriptions' : 
                      drilldownLevel === 'module' ? `Modules for ${selectedSimulatorType}` : 
                      'Sessions by Simulator Type'}
                   </h3>
                   <p className="text-muted-foreground text-sm">
                     {drilldownLevel === 'case' ? `Sessions by case for ${selectedModule}` :
                      drilldownLevel === 'module' ? 'Click on a module to drill down to cases' :
                      'Click on a simulator block to drill down to modules'}
                     <span className="text-primary font-medium"> • Last 12 Months Data</span>
                   </p>
                </div>
                 <div className="flex gap-2">
                   {drilldownLevel === 'module' && (
                     <Button variant="outline" onClick={resetSimulatorDrilldown} className="flex items-center gap-2">
                       <ArrowLeft className="w-4 h-4" />
                       Back to Simulators
                     </Button>
                   )}
                   {drilldownLevel === 'case' && (
                     <>
                       <Button variant="outline" onClick={() => setDrilldownLevel('module')} className="flex items-center gap-2">
                         <ArrowLeft className="w-4 h-4" />
                         Back to Modules
                       </Button>
                       <Button variant="ghost" onClick={resetSimulatorDrilldown} className="flex items-center gap-2 text-xs">
                         <ArrowLeft className="w-3 h-3" />
                         All Simulators
                       </Button>
                     </>
                   )}
                 </div>
              </div>
              
               <TreemapChart 
                 data={simulatorTypeData} 
                 onNodeClick={drilldownLevel === 'simulator' || drilldownLevel === 'module' ? handleSimulatorClick : undefined}
               />
            </Card>
          </TabsContent>
          
          <TabsContent value="sites">
            <Card className="p-6 shadow-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">
                    {drilldownLevel === 'site' ? 'Sessions by Sites' : 
                     drilldownLevel === 'site-simulator' ? `Simulators at ${selectedSite}` :
                     `Modules for ${selectedSiteSimulator} at ${selectedSite}`}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {drilldownLevel === 'site' ? 'Click on a site block to drill down to simulator types' : 
                     drilldownLevel === 'site-simulator' ? 'Click on a simulator to see modules' :
                     'Module-level session breakdown'}
                    <span className="text-primary font-medium"> • Last 12 Months Data</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  {drilldownLevel === 'site-simulator' && (
                    <Button variant="outline" onClick={resetSiteDrilldown} className="flex items-center gap-2">
                      <ArrowLeft className="w-4 h-4" />
                      Back to Sites
                    </Button>
                  )}
                  {drilldownLevel === 'site-module' && (
                    <Button variant="outline" onClick={() => setDrilldownLevel('site-simulator')} className="flex items-center gap-2">
                      <ArrowLeft className="w-4 h-4" />
                      Back to Simulators
                    </Button>
                  )}
                </div>
              </div>
              
              <TreemapChart 
                data={sitesData} 
                onNodeClick={drilldownLevel === 'site' || drilldownLevel === 'site-simulator' ? handleSiteClick : undefined}
              />
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};